import {
  initializePaddle,
  type Environments,
  type Paddle,
} from "@paddle/paddle-js";

const configuredEnvironment =
  import.meta.env.VITE_PADDLE_ENVIRONMENT?.trim() || "sandbox";

if (!["sandbox", "production"].includes(configuredEnvironment)) {
  throw new Error(
    `Invalid Paddle environment: ${configuredEnvironment}. Use "sandbox" or "production".`,
  );
}

export const paddleEnvironment = configuredEnvironment as Environments;
export const paddleClientToken =
  import.meta.env.VITE_PADDLE_CLIENT_TOKEN?.trim() || null;
export const isPaddleConfigured = paddleClientToken !== null;

const paddleCustomerPortalUrls = {
  production:
    "https://customer-portal.paddle.com/cpl_01kz8r0jcxmh0mp8tf0pxw297t",
  sandbox:
    "https://sandbox-customer-portal.paddle.com/cpl_01kzh6vmw6d592a1zsgdvbrwke",
} as const;

export function resolvePaddleCustomerPortalUrl(
  environment: Environments,
  configuredUrl?: string,
): string {
  const value = configuredUrl?.trim() || paddleCustomerPortalUrls[environment];
  const url = new URL(value);
  const expectedHostname =
    environment === "production"
      ? "customer-portal.paddle.com"
      : "sandbox-customer-portal.paddle.com";

  if (
    url.protocol !== "https:" ||
    url.hostname !== expectedHostname ||
    url.username ||
    url.password ||
    url.search ||
    url.hash ||
    !/^\/cpl_[a-z0-9]+\/?$/u.test(url.pathname)
  ) {
    throw new Error(
      `Invalid Paddle ${environment} customer portal URL: ${value}`,
    );
  }

  return url.toString();
}

export const paddleCustomerPortalUrl = resolvePaddleCustomerPortalUrl(
  paddleEnvironment,
  import.meta.env.VITE_PADDLE_CUSTOMER_PORTAL_URL,
);

const configuredConfiguratorUrl =
  import.meta.env.VITE_SAILPLOT_CONFIGURATOR_URL?.trim() ||
  "https://config.sailplot.app/";

export const paddleConfiguratorUrl = (() => {
  const url = new URL(configuredConfiguratorUrl);
  if (!["http:", "https:"].includes(url.protocol)) {
    throw new Error(
      `Invalid SailPlot configurator URL: ${configuredConfiguratorUrl}`,
    );
  }
  return url.toString();
})();

const optionalPriceId = (value: string | undefined, label: string) => {
  const priceId = value?.trim() || null;
  if (priceId && !/^pri_[a-z0-9]+$/u.test(priceId)) {
    throw new Error(`Invalid Paddle ${label} price ID: ${priceId}`);
  }
  return priceId;
};

export const paddlePriceIds = Object.freeze({
  personalCoach: optionalPriceId(
    import.meta.env.VITE_PADDLE_PERSONAL_COACH_PRICE_ID,
    "Personal, Coach & Sailing Team",
  ),
  clubCompany: optionalPriceId(
    import.meta.env.VITE_PADDLE_CLUB_COMPANY_PRICE_ID,
    "Club & Company",
  ),
});

export const isPaddleCheckoutConfigured = (
  priceId: string | null,
): priceId is string => isPaddleConfigured && priceId !== null;

let paddlePromise: Promise<Paddle | undefined> | null = null;

const assertTokenMatchesEnvironment = (token: string) => {
  const expectedPrefix = paddleEnvironment === "sandbox" ? "test_" : "live_";

  if (!token.startsWith(expectedPrefix)) {
    throw new Error(
      `The Paddle ${paddleEnvironment} environment requires a ${expectedPrefix} client-side token. Never use a Paddle API key in the browser.`,
    );
  }
};

/**
 * Lazily loads and initializes the shared Paddle.js instance.
 * Returns undefined while Paddle is intentionally not configured.
 */
export function initializePaddleClient(): Promise<Paddle | undefined> {
  if (!paddleClientToken) return Promise.resolve(undefined);

  assertTokenMatchesEnvironment(paddleClientToken);

  paddlePromise ??= initializePaddle({
    environment: paddleEnvironment,
    token: paddleClientToken,
  }).catch((error: unknown) => {
    paddlePromise = null;
    throw error;
  });

  return paddlePromise;
}
